import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../component/header";
import BannerImg from "../image/education.png";
import Banner from "../component/Banner";
import Footer from "../component/Footer";
import EducationOffer from "../component/EducationOffer";
import Philosophy from "../component/Philosophy";
import Resource from "../component/Resource";
import Community from "../component/Community";
import Spotlight from "../component/Spotlight";
import { UserDataContext } from "../context/User";
import CompletedEducation from "../component/CompletedEducation";
import RefineEducationFAQ from "../component/RefineEducationFAQ";
import Breadcrumb from "../component/Breadcrumb";
import { BookOpen, Briefcase, Eye, MessageCircle, Calendar, ArrowRight } from "lucide-react";
import { defaultRefinePages, fetchRefinePages } from "../utilis/refinePages";

const educationTracks = [
  {
    key: "technical",
    icon: BookOpen,
    label: "Technical Skills",
    description:
      "Hands-on learning for lighting, composition, post-processing, and the technical foundations of photography and sculpture.",
    color: "bg-cyan-50 text-cyan-700",
  },
  {
    key: "seeing",
    icon: Eye,
    label: "Improved Seeing",
    description:
      "Develop your visual intuition — how to observe, interpret light, find subjects, and develop a personal aesthetic voice.",
    color: "bg-purple-50 text-purple-700",
  },
  {
    key: "business",
    icon: Briefcase,
    label: "Business & Career",
    description:
      "Practical knowledge on pricing, licensing, presenting work, building an audience, and turning creativity into a sustainable practice.",
    color: "bg-amber-50 text-amber-700",
  },
];

function Education() {
  const { userData } = useContext(UserDataContext);
  const location = useLocation();
  const isRefinePage = location.pathname === "/refine";
  const completed = userData?.completedEducationActivities || userData?.educationActivities || [];
  const [refinePages, setRefinePages] = useState(defaultRefinePages);

  useEffect(() => {
    if (!isRefinePage) return undefined;

    let active = true;
    fetchRefinePages()
      .then((pages) => {
        if (active) setRefinePages(pages);
      })
      .catch(() => {
        if (active) setRefinePages(defaultRefinePages);
      });

    return () => {
      active = false;
    };
  }, [isRefinePage]);

  return (
    <div className="App">
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading={isRefinePage ? "Refine Education" : "Education & Creative Growth"}
        paragraph={
          isRefinePage
            ? "Find clear answers about Refine Education access, learning materials, resources, and member support."
            : "Lightweave Beacon Legacy believes that every artist deserves guidance, not from gatekeepers, but from mentors, peers, and shared learning. Our education programs provide structured study, tutorials, critique insights, and learning materials designed to elevate the craft."
        }
        minHeight={isRefinePage ? "55vh" : "100vh"}
        padding="80px 0"
        bton="Members please sign-in"
      />
      <Breadcrumb
        items={
          isRefinePage
            ? [{ label: "Education", to: "/education" }, { label: "Refine" }]
            : [{ label: "Education" }]
        }
      />

      {!isRefinePage && (
        <>
          {/* Three education tracks */}
          <section className="main-lwbl px-4 py-14">
            <div className="container m-auto">
              <div className="mb-10">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
                  Learning tracks
                </p>
                <h2 className="mt-2 font-cinzel text-[32px] text-[#101819] md:text-[44px]">
                  Three ways to grow
                </h2>
                <p className="mt-3 max-w-2xl font-outfit text-sm leading-7 text-[#536061]">
                  Education at LWBL is structured around what artists actually need — technical mastery,
                  sharper vision, and the practical skills to share their work with the world.
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-3">
                {educationTracks.map(({ key, icon: Icon, label, description, color }) => (
                  <article
                    key={key}
                    className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
                      <Icon size={20} />
                    </div>
                    <h3 className="font-cinzel text-xl text-[#111718]">{label}</h3>
                    <p className="mt-3 font-outfit text-sm leading-6 text-[#536061]">{description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Ask a Mentor + Upcoming Classes CTAs */}
          <section className="main-lwbl px-4 pb-10">
            <div className="container m-auto">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col justify-between rounded-2xl border border-cyan-100 bg-cyan-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700">
                      <MessageCircle size={20} />
                    </div>
                    <div>
                      <h3 className="font-cinzel text-xl text-[#111718]">Ask a Mentor</h3>
                      <p className="mt-2 font-outfit text-sm leading-6 text-[#536061]">
                        Have a question about your creative practice? Reach out to a mentor who has walked the path.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/mentor-messages"
                    className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
                  >
                    Go to mentor messages <ArrowRight size={14} />
                  </Link>
                </div>

                <div className="flex flex-col justify-between rounded-2xl border border-amber-100 bg-amber-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <h3 className="font-cinzel text-xl text-[#111718]">Upcoming Classes & Gatherings</h3>
                      <p className="mt-2 font-outfit text-sm leading-6 text-[#536061]">
                        Join live workshops, classes, and community sessions. See what is happening next.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/community"
                    className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-amber-600"
                  >
                    See upcoming events <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {isRefinePage && (
        <Resource
          resources={refinePages}
          maxItems={3}
          subHeading="Refine Areas"
          heading="Choose a Focus Area"
        />
      )}

      <RefineEducationFAQ />

      {!isRefinePage && (
        <>
          <EducationOffer />
          <CompletedEducation activities={completed} />
          <Philosophy />
          <Resource />
          <Community />
          <Spotlight />
        </>
      )}
      <Footer />
    </div>
  );
}

export default Education;
